import React, { useState, useRef, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import api from "../utils/api";
import axios from "axios";
import Select from "react-select"; // ← Native animations built-in
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  useMapEvents,
  useMap,
} from "react-leaflet";
import toGeoJSON from "@mapbox/togeojson";
import { DOMParser } from "xmldom";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import JSZip from "jszip";

// ===== Animations =====
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ===== Styled Components =====
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  animation: ${fadeUp} 300ms ease forwards;
`;

const ModalBox = styled.div`
  background: #fffdf9;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
  position: relative;
`;

const Title = styled.h2`
  margin-bottom: 16px;
  color: #1f2937;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  margin-bottom: 12px;
  border-radius: 10px;
  border: 1px solid #f97316;
  outline: none;
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  margin-bottom: 12px;
  border-radius: 10px;
  border: 1px solid #f97316;
  outline: none;
`;

const Button = styled.button`
  padding: 10px 16px;
  margin-right: 10px;
  background: linear-gradient(90deg, #ff7a2a 0%, #ffb06b 100%);
  color: #fff;
  font-weight: 700;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  &:hover {
    opacity: 0.9;
  }
`;

const Section = styled.div`
  margin-bottom: 20px;
`;
const Label = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  color: #444;
`;

const MapWrapper = styled.div`
  height: 400px;
  margin-bottom: 16px;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(249, 115, 22, 0.2);
`;

const WaypointRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
`;

const SmallInput = styled.input`
  width: 140px;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid #f97316;
`;

const GOOGLE_API_KEY = "AIzaSyCLZfYpmH-aAphq-XP6oOiYszqIq1hn-1E"; // Your key

const RouteModal = ({ isOpen, onClose, onSave, userId, userName }) => {
  const [routeName, setRouteName] = useState("");
  const [name, setName] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]); // Multi-select
  const [travelMode, setTravelMode] = useState("walk");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [search, setSearch] = useState("");
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [waypoints, setWaypoints] = useState([]);
  const [polyline, setPolyline] = useState([]);
  const [latInput, setLatInput] = useState("");
  const [lngInput, setLngInput] = useState("");
  const [selectedSurveyors, setSelectedSurveyors] = useState([]);
  const [kmlCoordinates, setKmlCoordinates] = useState([]); // ← ADD THIS LINE
  const [kmlFileId, setKmlFileId] = useState(null);
  const [shouldFitBounds, setShouldFitBounds] = useState(false);
  const mapRef = useRef();

  // ===== KML Upload =====

  const handleKMLUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return alert("Select a KML file");

    const formData = new FormData();
    formData.append("kml", file);

    try {
      const res = await api.post("/api/kml/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setKmlFileId(res?.data?.data?._id);

      // Extract coordinates from response
      const coordinates = res?.data?.data?.track?.coordinates;
      if (coordinates && Array.isArray(coordinates) && coordinates.length > 0) {
        // Store all coordinates
        setKmlCoordinates(coordinates);

        // Get first and last coordinates
        const firstCoord = coordinates[0];
        const lastCoord = coordinates[coordinates.length - 1];

        // Convert to Leaflet format {lat, lng}
        // Handle different coordinate formats: [lng, lat] or [lat, lng] or {latitude, longitude}
        let startPoint, endPoint;

        if (Array.isArray(firstCoord)) {
          // Array format: could be [lng, lat] (GeoJSON) or [lat, lng]
          // Assuming GeoJSON format [lng, lat] - adjust if needed
          startPoint = { lat: firstCoord[1], lng: firstCoord[0] };
          endPoint = { lat: lastCoord[1], lng: lastCoord[0] };
        } else if (firstCoord.latitude !== undefined) {
          // Object format: {latitude, longitude}
          startPoint = { lat: firstCoord.latitude, lng: firstCoord.longitude };
          endPoint = { lat: lastCoord.latitude, lng: lastCoord.longitude };
        } else if (firstCoord.lat !== undefined) {
          // Object format: {lat, lng}
          startPoint = { lat: firstCoord.lat, lng: firstCoord.lng };
          endPoint = { lat: lastCoord.lat, lng: lastCoord.lng };
        } else {
          alert("Unknown coordinate format");
          return;
        }

        // Convert all coordinates to polyline points
        const polylinePoints = coordinates.map((coord) => {
          if (Array.isArray(coord)) {
            return [coord[1], coord[0]]; // [lat, lng] for Leaflet
          } else if (coord.latitude !== undefined) {
            return [coord.latitude, coord.longitude];
          } else {
            return [coord.lat, coord.lng];
          }
        });

        // Set start and end markers
        setStart(startPoint);
        setEnd(endPoint);
        setPolyline(polylinePoints);

        // Trigger map centering
        setShouldFitBounds(true);
      }

      alert("KML uploaded successfully");
    } catch (err) {
      alert(err.response?.data?.message || "Upload failed");
    }
  };

  // ===== Search City =====
  // const handleSearch = async () => {
  //   if (!search) return;
  //   try {
  //     const res = await axios.get(
  //       `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
  //         search
  //       )}&key=${GOOGLE_API_KEY}`
  //     );
  //     const loc = res.data.results[0]?.geometry?.location;
  //     if (loc && mapRef.current) {
  //       mapRef.current.setView([loc.lat, loc.lng], 13);
  //     }
  //   } catch (err) {
  //     console.error("Search failed:", err);
  //   }
  // };

  // ===== Map Click Handler =====
  // const MapClickHandler = () => {
  //   useMapEvents({
  //     click(e) {
  //       const coords = e.latlng;
  //       if (!start) {
  //         setStart(coords);
  //         setEnd(null);
  //         setPolyline([]);
  //         return;
  //       }
  //       if (!end) {
  //         setEnd(coords);
  //         fetchDirections(start, coords);
  //         return;
  //       }
  //       // Reset
  //       setStart(coords);
  //       setEnd(null);
  //       setPolyline([]);
  //     },
  //   });
  //   return null;
  // };

  // ===== Map Fit Bounds Handler =====
  const MapFitBounds = ({ polyline, shouldFit }) => {
    const map = useMap();

    useEffect(() => {
      if (shouldFit && polyline && polyline.length > 0) {
        const bounds = L.latLngBounds(polyline);
        map.fitBounds(bounds, { padding: [50, 50] });
        setShouldFitBounds(false); // Reset flag after fitting
      }
    }, [shouldFit, polyline, map]);

    return null;
  };

  // ===== Fetch Directions (Based on Travel Mode) =====
  // const fetchDirections = async (from, to) => {
  //   try {
  //     const modeMap = { walk: "walking", bike: "bicycling", car: "driving" };
  //     const mode = modeMap[travelMode] || "walking";
  //     const url = `https://maps.googleapis.com/maps/api/directions/json?origin=${from.lat},${from.lng}&destination=${to.lat},${to.lng}&mode=${mode}&key=${GOOGLE_API_KEY}`;
  //     const res = await axios.get(url);
  //     const routes = res.data?.routes || [];
  //     if (routes.length === 0) {
  //       setPolyline([
  //         [from.lat, from.lng],
  //         [to.lat, to.lng],
  //       ]);
  //       return;
  //     }
  //     const points = decodePolyline(routes[0].overview_polyline.points);
  //     setPolyline(points.map((p) => [p.latitude, p.longitude]));
  //   } catch (err) {
  //     console.error("Routing failed:", err);
  //   }
  // };

  // ===== Decode Polyline =====
  // const decodePolyline = (t) => {
  //   if (!t) return [];
  //   let index = 0,
  //     lat = 0,
  //     lng = 0,
  //     coordinates = [];
  //   while (index < t.length) {
  //     let b,
  //       shift = 0,
  //       result = 0;
  //     do {
  //       b = t.charCodeAt(index++) - 63;
  //       result |= (b & 0x1f) << shift;
  //       shift += 5;
  //     } while (b >= 0x20);
  //     const dlat = result & 1 ? ~(result >> 1) : result >> 1;
  //     lat += dlat;
  //     shift = 0;
  //     result = 0;
  //     do {
  //       b = t.charCodeAt(index++) - 63;
  //       result |= (b & 0x1f) << shift;
  //       shift += 5;
  //     } while (b >= 0x20);
  //     const dlng = result & 1 ? ~(result >> 1) : result >> 1;
  //     lng += dlng;
  //     coordinates.push({ latitude: lat / 1e5, longitude: lng / 1e5 });
  //   }
  //   return coordinates;
  // };

  // ===== Add Manual Waypoint =====
  const addWaypoint = () => {
    const lat = parseFloat(latInput);
    const lng = parseFloat(lngInput);
    if (isNaN(lat) || isNaN(lng)) return alert("Invalid coordinates");
    setWaypoints([
      ...waypoints,
      { latitude: lat, longitude: lng, order: waypoints.length + 1 },
    ]);
    setLatInput("");
    setLngInput("");
  };

  // ===== Remove Waypoint =====
  const removeWaypoint = (index) => {
    setWaypoints(waypoints.filter((_, i) => i !== index));
  };

  // ===== Save Route (Full Phase 2 Payload) =====
  const handleSave = async () => {
    try {
      if (!routeName.trim()) return alert("Route name is required");
      if (!start || !end) return alert("Please set start and end points");
      // if (selectedUsers.length === 0) return alert("Assign at least one surveyor");

      // const assignedUserIds = selectedUsers.map(user => user.value);

      const payload = {
        name: routeName.trim(),
        // assignedUsers: assignedUserIds,
        // travelMode: travelMode.toLowerCase(),

        // THIS IS THE FIX — CONVERT lat/lng → latitude/longitude
        startLocation: {
          latitude: start.lat,
          longitude: start.lng,
        },
        endLocation: {
          latitude: end.lat,
          longitude: end.lng,
        },

        waypoints: waypoints,
        kmlCoordinates: kmlCoordinates.length > 0 ? kmlCoordinates : undefined,
        routeGeometry: polyline.length > 0 ? polyline : undefined,
        kmlFile: kmlFileId ? kmlFileId : undefined,
      };

      await api.post("/api/dataanalysis/routes", payload);
      alert("Route saved successfully!");
      onClose();
      onSave();
    } catch (err) {
      console.error("Save error:", err.response?.data || err);
      alert("Failed to save route");
    }
  };

  if (!isOpen) return null;

  const startIcon = new L.Icon({
    iconUrl: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
    iconSize: [40, 40],
  });
  const endIcon = new L.Icon({
    iconUrl: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
    iconSize: [40, 40],
  });

  return (
    <Overlay>
      <ModalBox>
        <Title>Create Route</Title>
        <CloseBtn onClick={onClose}>✖</CloseBtn>

        {/* Route Name */}
        <Input
          type="text"
          placeholder="Route Name"
          value={routeName}
          onChange={(e) => setRouteName(e.target.value)}
        />

        {/* KML Upload */}
        <Section>
          <Label>Upload KML/KMZ File (Optional - Auto-draws Route)</Label>
          <Input type="file" accept=".kml,.kmz" onChange={handleKMLUpload} />
        </Section>

        {/* Search + Map */}
        {/* <div style={{ display: "flex", marginBottom: "12px", gap: "8px" }}>
          <Input
            type="text"
            placeholder="Search city or area"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button onClick={handleSearch}>Search</Button>
        </div> */}

        <MapWrapper>
          <MapContainer
            center={[26.1445, 91.7362]}
            zoom={12}
            style={{ height: "100%", width: "100%" }}
            whenCreated={(mapInstance) => (mapRef.current = mapInstance)}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
            />
            {/* Note: Redundant MapClickHandler because we are using MapFitBounds */}
            {/* <MapClickHandler /> */}
            <MapFitBounds polyline={polyline} shouldFit={shouldFitBounds} />
            {start && <Marker position={start} icon={startIcon} />}
            {end && <Marker position={end} icon={endIcon} />}
            {polyline.length > 0 && (
              <Polyline positions={polyline} color="#f97316" weight={4} />
            )}
          </MapContainer>
        </MapWrapper>

        {/* Manual Waypoints */}
        <Section>
          <Label>Manual Lat/Lng Input (Optional)</Label>
          <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
            <SmallInput
              placeholder="Latitude"
              value={latInput}
              onChange={(e) => setLatInput(e.target.value)}
            />
            <SmallInput
              placeholder="Longitude"
              value={lngInput}
              onChange={(e) => setLngInput(e.target.value)}
            />
            <Button onClick={addWaypoint}>Add Point</Button>
          </div>
          {waypoints.map((wp, i) => (
            <WaypointRow key={i}>
              <span>
                {i + 1}. {wp.latitude.toFixed(6)}, {wp.longitude.toFixed(6)}
              </span>
              <Button
                onClick={() => removeWaypoint(i)}
                style={{
                  background: "#ef4444",
                  marginLeft: "auto",
                  padding: "4px 8px",
                }}
              >
                Remove
              </Button>
            </WaypointRow>
          ))}
        </Section>

        <div>
          <Button onClick={handleSave}>Save Route</Button>
          <Button style={{ background: "#ef4444" }} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </ModalBox>
    </Overlay>
  );
};

export default RouteModal;

// import React, { useState, useRef, useEffect } from "react";
// import styled, { keyframes } from "styled-components";
// import api from "../utils/api"; // ✅ centralized axios instance
// import axios from "axios";
// import { MapContainer, TileLayer, Marker, Polyline, useMapEvents } from "react-leaflet";
// import "leaflet/dist/leaflet.css";
// import L from "leaflet";

// // ===== Animations =====
// const fadeUp = keyframes`
//   from { opacity: 0; transform: translateY(12px); }
//   to   { opacity: 1; transform: translateY(0); }
// `;

// // ===== Styled Components =====
// const Overlay = styled.div`
//   position: fixed;
//   inset: 0;
//   background: rgba(0,0,0,0.5);
//   display: flex;
//   justify-content: center;
//   align-items: center;
//   z-index: 999;
//   animation: ${fadeUp} 300ms ease forwards;
// `;

// const ModalBox = styled.div`
//   background: #fffdf9;
//   border-radius: 16px;
//   padding: 24px;
//   width: 90%;
//   max-width: 800px;
//   max-height: 90vh;
//   overflow-y: auto;
//   box-shadow: 0 12px 30px rgba(0,0,0,0.15);
//   position: relative;
// `;

// const Title = styled.h2`
//   margin-bottom: 16px;
//   color: #1f2937;
// `;

// const CloseBtn = styled.button`
//   position: absolute;
//   top: 12px;
//   right: 12px;
//   background: transparent;
//   border: none;
//   font-size: 20px;
//   cursor: pointer;
// `;

// const Input = styled.input`
//   width: 100%;
//   padding: 10px 12px;
//   margin-bottom: 12px;
//   border-radius: 10px;
//   border: 1px solid #f97316;
//   outline: none;
// `;

// const Select = styled.select`
//   width: 100%;
//   padding: 10px 12px;
//   margin-bottom: 12px;
//   border-radius: 10px;
//   border: 1px solid #f97316;
//   outline: none;
// `;

// const Button = styled.button`
//   padding: 10px 16px;
//   margin-right: 10px;
//   background: linear-gradient(90deg,#ff7a2a 0%, #ffb06b 100%);
//   color: #fff;
//   font-weight: 700;
//   border: none;
//   border-radius: 10px;
//   cursor: pointer;
//   &:hover {
//     opacity: 0.9;
//   }
// `;

// const MapWrapper = styled.div`
//   width: 100%;
//   height: 400px;
//   margin-bottom: 16px;
//   border-radius: 14px;
//   overflow: hidden;
//   border: 1px solid rgba(249,115,22,0.2);
// `;

// // ===== RouteModal Component =====
// const GOOGLE_API_KEY = 'AIzaSyCLZfYpmH-aAphq-XP6oOiYszqIq1hn-1E';

// const RouteModal = ({ isOpen, onClose, onSave, userId, userName }) => {
//   const [routeName, setRouteName] = useState("");
//   const [assignedUser, setAssignedUser] = useState(userId || "");
//   const [users, setUsers] = useState([]);
//   const [loadingUsers, setLoadingUsers] = useState(true);
//   const [search, setSearch] = useState("");
//   const [start, setStart] = useState(null);
//   const [end, setEnd] = useState(null);
//   const [polyline, setPolyline] = useState([]);
//   const mapRef = useRef();

//  // ===== Fetch Users (FIXED) =====
// useEffect(() => {
//   if (!isOpen) return;

//   const fetchSurveyors = async () => {
//     try {
//       setLoadingUsers(true);

//       const res = await api.get("/api/dataanalysis/surveyors");

//       console.log("API Response:", res.data); // You already see this

//       // EXACT CORRECT EXTRACTION — DO NOT CHANGE THIS LINE
//       const surveyorList = res.data?.data || [];

//       if (!Array.isArray(surveyorList)) {
//         console.error("Not an array:", surveyorList);
//         setUsers([]);
//         setLoadingUsers(false);
//         return;
//       }

//       // Optional: double-check role (safe even if not in payload)
//       const filtered = surveyorList.filter(user =>
//         user.role === "surveyor" || !user.role // fallback: accept if role missing
//       );

//       console.log("Surveyors for dropdown:", filtered);

//       setUsers(filtered);

//       // Auto-select if userId passed (from SingleUser page)
//       if (userId && filtered.some(u => u._id === userId)) {
//         setAssignedUser(userId);
//       }

//     } catch (err) {
//       console.error("Fetch failed:", err);
//       setUsers([]);
//     } finally {
//       setLoadingUsers(false);
//     }
//   };

//   fetchSurveyors();
// }, [isOpen, userId]);

//   useEffect(() => {
//     if (userId) setAssignedUser(userId);
//   }, [userId]);

//   if (!isOpen) return null;

//   // ===== Search city/location =====
//   const handleSearch = async () => {
//     if (!search) return;
//     try {
//       const res = await axios.get(
//         `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
//           search
//         )}&key=${GOOGLE_API_KEY}`
//       );
//       const loc = res.data.results[0]?.geometry?.location;
//       if (loc && mapRef.current) {
//         mapRef.current.setView([loc.lat, loc.lng], 13);
//       }
//     } catch (err) {
//       console.error("Search failed:", err);
//     }
//   };

//   // ===== Handle map clicks =====
//   const MapClickHandler = () => {
//     useMapEvents({
//       click(e) {
//         const coords = e.latlng;
//         if (!start) {
//           setStart(coords);
//           setEnd(null);
//           setPolyline([]);
//           return;
//         }
//         if (!end) {
//           setEnd(coords);
//           fetchDirections(start, coords);
//           return;
//         }
//         setStart(coords);
//         setEnd(null);
//         setPolyline([]);
//       },
//     });
//     return null;
//   };

//   // ===== Fetch directions from Google =====
//   const fetchDirections = async (from, to) => {
//     try {
//       const url = `https://maps.googleapis.com/maps/api/directions/json?origin=${from.lat},${from.lng}&destination=${to.lat},${to.lng}&mode=walking&key=${GOOGLE_API_KEY}`;
//       const res = await axios.get(url);
//       const routes = Array.isArray(res.data?.routes) ? res.data.routes : [];
//       if (routes.length === 0) {
//         setPolyline([[from.lat, from.lng], [to.lat, to.lng]]);
//         return;
//       }
//       const points = decodePolyline(routes[0].overview_polyline.points);
//       setPolyline(points.map((p) => [p.latitude, p.longitude]));
//     } catch (err) {
//       console.error("Failed to fetch route:", err);
//     }
//   };

//   // ===== Decode Polyline =====
//   const decodePolyline = (t) => {
//     if (!t) return [];
//     let index = 0, lat = 0, lng = 0, coordinates = [];
//     while (index < t.length) {
//       let b, shift = 0, result = 0;
//       do { b = t.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
//       const dlat = result & 1 ? ~(result >> 1) : result >> 1; lat += dlat;
//       shift = 0; result = 0;
//       do { b = t.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
//       const dlng = result & 1 ? ~(result >> 1) : result >> 1; lng += dlng;
//       coordinates.push({ latitude: lat / 1e5, longitude: lng / 1e5 });
//     }
//     return coordinates;
//   };

//   // ===== Save Route =====
//          const handleSave = async () => {
//                 if (!routeName.trim()) {
//                       alert("Please enter Route Name");
//                       return;
//                 }
//                 if (!assignedUser) {
//                       alert("Please select a surveyor");
//                       return;
//                 }
//                 if (!start || !end) {
//                       alert("Please click on map to set Start and End points");
//                       return;
//                 }

//                 const payload = {
//                       name: routeName.trim(),
//                       assignedTo: assignedUser,                    // ← EXACT FIELD NAME
//                   startLocation: {
//                         latitude: start.lat,
//                         longitude: start.lng
//                   },
//                   endLocation: {
//                       latitude: end.lat,
//                       longitude: end.lng
//                   }
//                 };

//                 console.log("Sending payload:", payload); // ← CHECK THIS IN CONSOLE

//                 try {
//                     const res = await api.post("/api/dataanalysis/routes", payload);  // ← /routes (not /route)

//                     console.log("Route created:", res.data);
//                     alert("Route created & assigned successfully!");
//                     onSave(res.data.route);
//                     onClose();
//                 } catch (err) {
//                     console.error("Save failed:", err.response?.data || err);
//                     alert(err.response?.data?.message || "Failed to save route");
//                 }
//           };

//   // ===== Icons =====
//   const startIcon = new L.Icon({
//     iconUrl: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
//     iconSize: [40, 40],
//   });
//   const endIcon = new L.Icon({
//     iconUrl: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
//     iconSize: [40, 40],
//   });

//   return (
//     <Overlay>
//       <ModalBox>
//         <Title>Create Route</Title>
//         <CloseBtn onClick={onClose}>✖</CloseBtn>

//         <Input
//           type="text"
//           placeholder="Route Name"
//           value={routeName}
//           onChange={(e) => setRouteName(e.target.value)}
//         />

//         <Select
//                 value={assignedUser}
//                 onChange={(e) => setAssignedUser(e.target.value)}
//                 disabled={loadingUsers}
//               >
//                 <option value="">
//                   {loadingUsers
//                     ? "Loading surveyors..."
//                     : users.length === 0
//                       ? "No surveyors found"
//                       : "Select a surveyor"}
//                 </option>

//                 {users.map((user) => (
//                   <option key={user._id} value={user._id}>
//                     {user.name} – {user.organization?.orgName || "No Org"} ({user.email})
//                   </option>
//                 ))}
//           </Select>

//         <div style={{ display: "flex", marginBottom: "12px", gap: "8px" }}>
//                 <Input
//                   type="text"
//                   placeholder="Search city or area"
//                   value={search}
//                   onChange={(e) => setSearch(e.target.value)}
//                 />
//                 <Button onClick={handleSearch}>Search</Button>
//         </div>

//         <MapWrapper>
//           <MapContainer
//             center={[26.1445, 91.7362]}
//             zoom={12}
//             style={{ height: "100%", width: "100%" }}
//             whenCreated={(mapInstance) => (mapRef.current = mapInstance)}
//           >
//             <TileLayer
//               url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
//               attribution="&copy; OpenStreetMap contributors"
//             />
//             <MapClickHandler />
//             {start && <Marker position={start} icon={startIcon} />}
//             {end && <Marker position={end} icon={endIcon} />}
//             {polyline.length > 0 && <Polyline positions={polyline} color="blue" />}
//           </MapContainer>
//         </MapWrapper>

//         <div>
//           <Button onClick={handleSave}>Save Route</Button>
//           <Button style={{ background: "#ef4444" }} onClick={onClose}>
//             Cancel
//           </Button>
//         </div>
//       </ModalBox>
//     </Overlay>
//   );
// };

// export default RouteModal;
